import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import styled from "styled-components";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Upload,
  Button,
  message,
  Steps,
  Card,
  Space,
  Divider,
  Typography,
  Checkbox,
  Row,
  Col,
  Alert,
  Tooltip,
} from "antd";
import {
  UploadOutlined,
  FileTextOutlined,
  SignatureOutlined,
  CheckCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { device } from "../lib/breakpoints";
import leaseModel from "../lib/firebase/leaseModel";
import unitModel from "../lib/firebase/unitModel";
import { listTenants } from "../lib/firebase/tenantModel";
import { getProperties } from "../lib/api/propertiesApi";
import { getLeaseCompliance } from "../lib/api/leaseComplianceApi";
import { getUnitDisplayAddress } from "../lib/unitAddressUtils";

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;
const { Step } = Steps;

function stateDepositValid(
  monthlyRent,
  securityDeposit,
  securityDepositMonths
) {
  const rent = Number(monthlyRent);
  const dep = Number(securityDeposit);
  if (!rent || rent <= 0) return true;
  if (securityDepositMonths == null) return true;
  return dep <= rent * securityDepositMonths;
}

const LeaseModal = ({
  isOpen,
  onClose,
  onSuccess,
  editLease = null,
  ownerId,
}) => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [units, setUnits] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");
  const [signatureFields, setSignatureFields] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [properties, setProperties] = useState([]);
  const [complianceConfig, setComplianceConfig] = useState(null);
  const fileInputRef = useRef();

  const unitId = Form.useWatch("unitId", form);
  const monthlyRent = Form.useWatch("monthlyRent", form);
  const securityDeposit = Form.useWatch("securityDeposit", form);
  const propertyState =
    selectedUnit?.state ??
    (
      selectedUnit?.propertyId &&
      properties.find((p) => p.id === selectedUnit.propertyId)
    )?.state ??
    null;
  const depositValid = stateDepositValid(
    monthlyRent,
    securityDeposit,
    complianceConfig?.securityDepositMonths
  );

  const unitSelectOptions = useMemo(
    () =>
      (units || []).map((unit) => {
        const addr = getUnitDisplayAddress(unit);
        const label =
          [unit.name, addr].filter(Boolean).join(" - ") ||
          unit.unitNumber ||
          unit.id;
        return { value: unit.id, label };
      }),
    [units]
  );

  const tenantSelectOptions = useMemo(
    () =>
      (tenants || []).map((t) => ({
        value: t.id,
        label:
          [t.firstName, t.lastName].filter(Boolean).join(" ").trim() ||
          t.name ||
          t.email ||
          "Unnamed tenant",
      })),
    [tenants]
  );

  const loadUnitsAndTenants = useCallback(async () => {
    try {
      setLoading(true);
      const [unitsData, tenantsData, propsRes] = await Promise.all([
        unitModel.getByOwner(ownerId),
        listTenants(),
        getProperties().catch(() => ({ success: false, properties: [] })),
      ]);
      setUnits(unitsData || []);
      setTenants(tenantsData?.tenants || []);
      setProperties(propsRes?.properties || []);
    } catch (error) {
      console.error("Error loading units and tenants:", error);
      message.error("Failed to load doors and tenants");
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  const loadEditData = useCallback(() => {
    if (editLease) {
      form.setFieldsValue({
        leaseNumber: editLease.leaseNumber,
        unitId: editLease.unitId,
        tenantId: editLease.tenantId,
        startDate: editLease.startDate ? new Date(editLease.startDate) : null,
        endDate: editLease.endDate ? new Date(editLease.endDate) : null,
        monthlyRent: editLease.monthlyRent,
        securityDeposit: editLease.securityDeposit,
        terms: editLease.terms || {},
      });
      setPdfUrl(editLease.pdfUrl || "");
      setSignatureFields(editLease.signatureFields || []);
      setCurrentStep(editLease.pdfUrl ? 1 : 0);
    }
  }, [editLease, form]);

  // Load units and tenants when modal opens
  useEffect(() => {
    if (isOpen && ownerId) {
      loadUnitsAndTenants();
    }
  }, [isOpen, ownerId, loadUnitsAndTenants]);

  // Load edit data if editing
  useEffect(() => {
    if (isOpen && editLease) {
      loadEditData();
    }
  }, [isOpen, editLease, loadEditData]);

  // Keep selectedUnit in sync when unitId changes (e.g. from edit or form)
  useEffect(() => {
    if (unitId && units.length) {
      const u = units.find((x) => x.id === unitId);
      setSelectedUnit(u || null);
    } else {
      setSelectedUnit(null);
    }
  }, [unitId, units]);

  // Load compliance config when property state is known
  useEffect(() => {
    if (!propertyState) {
      setComplianceConfig(null);
      return;
    }
    getLeaseCompliance(propertyState)
      .then(setComplianceConfig)
      .catch(() => setComplianceConfig(null));
  }, [propertyState]);

  const handleUnitChange = (id) => {
    const unit = units.find((u) => u.id === id);
    setSelectedUnit(unit || null);

    if (unit && unit.monthlyRent) {
      form.setFieldsValue({ monthlyRent: unit.monthlyRent });
    }
  };

  const handleTenantChange = (tenantId) => {
    const tenant = tenants?.find((t) => t.id === tenantId);
    setSelectedTenant(tenant);
  };

  const handleFileUpload = (file) => {
    const isPDF = file.type === "application/pdf";
    if (!isPDF) {
      message.error("You can only upload PDF files!");
      return false;
    }

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error("PDF must be smaller than 10MB!");
      return false;
    }

    setPdfFile(file);
    return false; // Prevent default upload behavior
  };

  const handlePDFUpload = async () => {
    if (!pdfFile) {
      message.error("Please select a PDF file first");
      return;
    }

    try {
      setUploading(true);

      // Create lease first if it doesn't exist
      let leaseId = editLease?.id;
      if (!leaseId) {
        const values = await form.validateFields();
        const leaseData = {
          ...values,
          ownerId,
          startDate: values.startDate?.toDate(),
          endDate: values.endDate?.toDate(),
          status: "draft",
        };

        const newLease = await leaseModel.create(leaseData);
        leaseId = newLease.id;
      }

      // Upload PDF
      const uploadedUrl = await leaseModel.uploadPDF(leaseId, pdfFile);
      setPdfUrl(uploadedUrl);

      message.success("PDF uploaded successfully!");
      setCurrentStep(1);
    } catch (error) {
      console.error("Error uploading PDF:", error);
      message.error("Failed to upload PDF");
    } finally {
      setUploading(false);
    }
  };

  const addSignatureField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      x: 100,
      y: 100,
      page: 1,
      signerType: "tenant",
      signerId: selectedTenant?.id || "",
      required: true,
      signed: false,
      label: "Tenant Signature",
    };

    setSignatureFields([...signatureFields, newField]);
  };

  const updateSignatureField = (fieldId, updates) => {
    setSignatureFields((fields) =>
      fields.map((field) =>
        field.id === fieldId ? { ...field, ...updates } : field
      )
    );
  };

  const removeSignatureField = (fieldId) => {
    setSignatureFields((fields) =>
      fields.filter((field) => field.id !== fieldId)
    );
  };

  const handleSaveSignatureFields = async () => {
    if (signatureFields.length === 0) {
      message.error("Please add at least one signature field");
      return;
    }

    try {
      setLoading(true);

      // Update lease with signature fields
      await leaseModel.update(editLease?.id, {
        signatureFields,
        status: "pending_signature",
      });

      message.success("Signature fields saved successfully!");
      setCurrentStep(2);
    } catch (error) {
      console.error("Error saving signature fields:", error);
      message.error("Failed to save signature fields");
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = async () => {
    try {
      setLoading(true);

      if (editLease) {
        // Update existing lease
        await leaseModel.update(editLease.id, {
          signatureFields,
          status: "pending_signature",
        });
        message.success("Lease updated successfully!");
      } else {
        // Create new lease
        const values = await form.validateFields();
        const leaseData = {
          ...values,
          ownerId,
          startDate: values.startDate?.toDate(),
          endDate: values.endDate?.toDate(),
          signatureFields,
          status: "pending_signature",
        };

        await leaseModel.create(leaseData);
        message.success("Lease created successfully!");
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error saving lease:", error);
      message.error("Failed to save lease");
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      title: "Basic Info",
      icon: <FileTextOutlined />,
    },
    {
      title: "Upload PDF",
      icon: <UploadOutlined />,
    },
    {
      title: "Signature Fields",
      icon: <SignatureOutlined />,
    },
    {
      title: "Complete",
      icon: <CheckCircleOutlined />,
    },
  ];

  const renderBasicInfo = () => (
    <div>
      <Title level={4}>Lease Information</Title>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="leaseNumber"
            label="Lease Number"
            rules={[{ required: true, message: "Please enter lease number" }]}
          >
            <Input placeholder="e.g., LEASE-2024-001" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="unitId"
            label="Door"
            rules={[{ required: true, message: "Please select a door" }]}
          >
            <Select
              placeholder="Select door"
              onChange={handleUnitChange}
              showSearch
              optionFilterProp="label"
              options={unitSelectOptions}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="tenantId"
            label="Tenant"
            rules={[{ required: true, message: "Please select a tenant" }]}
          >
            <Select
              placeholder="Select tenant"
              onChange={handleTenantChange}
              showSearch
              optionFilterProp="label"
              options={tenantSelectOptions}
              notFoundContent={
                tenantSelectOptions.length === 0
                  ? "No tenants yet - add tenants from the Tenants page first."
                  : undefined
              }
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="monthlyRent"
            label="Monthly Rent"
            rules={[{ required: true, message: "Please enter monthly rent" }]}
          >
            <InputNumber
              placeholder="0.00"
              min={0}
              precision={2}
              style={{ width: "100%" }}
              prefix="$"
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="startDate"
            label="Start Date"
            rules={[{ required: true, message: "Please select start date" }]}
          >
            <DatePicker
              style={{ width: "100%" }}
              placeholder="Select start date"
              format="MM/DD/YYYY"
              picker="date"
              showToday={true}
              showTime={false}
              allowClear={true}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="endDate"
            label="End Date"
            rules={[{ required: true, message: "Please select end date" }]}
          >
            <DatePicker
              style={{ width: "100%" }}
              placeholder="Select end date"
              format="MM/DD/YYYY"
              picker="date"
              showToday={true}
              showTime={false}
              allowClear={true}
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="securityDeposit" label="Security Deposit">
        <InputNumber
          placeholder="0.00"
          min={0}
          precision={2}
          style={{ width: "100%" }}
          prefix="$"
        />
      </Form.Item>

      {propertyState && (
        <Card
          size="small"
          title="State Compliance Status"
          style={{ marginBottom: 16 }}
        >
          <div style={{ marginBottom: 8 }}>
            {(complianceConfig?.disclosures || []).map((label) => (
              <div key={label} style={{ marginBottom: 4 }}>
                <Checkbox checked disabled /> {label}
              </div>
            ))}
            <div style={{ marginBottom: 4 }}>
              <Checkbox checked disabled /> Lead-Based Paint (if pre-1978)
            </div>
          </div>
          {complianceConfig?.securityDepositMonths != null && (
            <Alert
              type={depositValid ? "success" : "error"}
              showIcon
              message={
                depositValid
                  ? `Deposit within state cap (${complianceConfig.securityDepositMonths} month(s) rent)`
                  : "Security deposit exceeds state limit"
              }
              description={
                !depositValid ? complianceConfig?.depositStatute : null
              }
            />
          )}
        </Card>
      )}

      {!propertyState && (
        <Alert
          type="info"
          showIcon
          message="Select a door to see state-specific compliance and disclosures for the generated lease."
          style={{ marginBottom: 16 }}
        />
      )}

      <Row gutter={16} className="lease-terms-row">
        <Col span={8}>
          <Form.Item name={["terms", "utilities"]} label="Utilities">
            <Select placeholder="Who pays utilities">
              <Option value="tenant">Tenant pays</Option>
              <Option value="landlord">Landlord pays</Option>
              <Option value="split">Split</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name={["terms", "pets"]}
            label="Pets Allowed"
            valuePropName="checked"
            className="pets-allowed-field"
          >
            <Checkbox>Yes</Checkbox>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name={["terms", "lateFees"]} label="Late Fee">
            <InputNumber
              placeholder="0.00"
              min={0}
              precision={2}
              style={{ width: "100%" }}
              prefix="$"
            />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );

  const renderPDFUpload = () => (
    <div>
      <Title level={4}>Upload Lease PDF</Title>
      <Text type="secondary">
        Upload the lease document that tenants will sign. You'll be able to add
        signature fields in the next step.
      </Text>

      <Card style={{ marginTop: 16 }}>
        <Upload
          beforeUpload={handleFileUpload}
          fileList={pdfFile ? [pdfFile] : []}
          onRemove={() => setPdfFile(null)}
          accept=".pdf"
        >
          <Button icon={<UploadOutlined />} size="large">
            Select PDF File
          </Button>
        </Upload>

        {pdfFile && (
          <div style={{ marginTop: 16 }}>
            <Button
              type="primary"
              onClick={handlePDFUpload}
              loading={uploading}
              icon={<UploadOutlined />}
            >
              Upload PDF
            </Button>
          </div>
        )}

        {pdfUrl && (
          <Alert
            message="PDF uploaded successfully!"
            description="You can now proceed to add signature fields."
            type="success"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </Card>
    </div>
  );

  const renderSignatureFields = () => (
    <div>
      <Title level={4}>Signature Fields</Title>
      <Text type="secondary">
        Define where tenants and landlords need to sign on the PDF document.
      </Text>

      <div style={{ marginTop: 16 }}>
        <Button
          type="dashed"
          onClick={addSignatureField}
          icon={<PlusOutlined />}
          style={{ width: "100%", marginBottom: 16 }}
        >
          Add Signature Field
        </Button>

        {signatureFields.map((field, index) => (
          <Card key={field.id} style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item label="Signer Type">
                  <Select
                    value={field.signerType}
                    onChange={(value) =>
                      updateSignatureField(field.id, { signerType: value })
                    }
                  >
                    <Option value="tenant">Tenant</Option>
                    <Option value="landlord">Landlord</Option>
                    <Option value="guarantor">Guarantor</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Page">
                  <InputNumber
                    value={field.page}
                    onChange={(value) =>
                      updateSignatureField(field.id, { page: value })
                    }
                    min={1}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="X Position">
                  <InputNumber
                    value={field.x}
                    onChange={(value) =>
                      updateSignatureField(field.id, { x: value })
                    }
                    min={0}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Y Position">
                  <InputNumber
                    value={field.y}
                    onChange={(value) =>
                      updateSignatureField(field.id, { y: value })
                    }
                    min={0}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Label">
                  <Input
                    value={field.label}
                    onChange={(e) =>
                      updateSignatureField(field.id, { label: e.target.value })
                    }
                    placeholder="e.g., Tenant Signature"
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Required">
                  <Checkbox
                    checked={field.required}
                    onChange={(e) =>
                      updateSignatureField(field.id, {
                        required: e.target.checked,
                      })
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeSignatureField(field.id)}
                >
                  Remove
                </Button>
              </Col>
            </Row>
          </Card>
        ))}

        {signatureFields.length > 0 && (
          <Button
            type="primary"
            onClick={handleSaveSignatureFields}
            loading={loading}
            style={{ marginTop: 16 }}
          >
            Save Signature Fields
          </Button>
        )}
      </div>
    </div>
  );

  const renderComplete = () => (
    <div style={{ textAlign: "center" }}>
      <CheckCircleOutlined
        style={{ fontSize: 64, color: "#52c41a", marginBottom: 16 }}
      />
      <Title level={3}>Lease Setup Complete!</Title>
      <Text type="secondary">
        Your lease has been created and is ready for signatures. Tenants will
        receive a notification to sign the document.
      </Text>

      <div style={{ marginTop: 24 }}>
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => window.open(pdfUrl, "_blank")}
          >
            View PDF
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => window.open(pdfUrl, "_blank")}
          >
            Download
          </Button>
        </Space>
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderBasicInfo();
      case 1:
        return renderPDFUpload();
      case 2:
        return renderSignatureFields();
      case 3:
        return renderComplete();
      default:
        return renderBasicInfo();
    }
  };

  const handleNext = async () => {
    if (currentStep === 0) {
      try {
        await form.validateFields();
        setCurrentStep(1);
      } catch (error) {
        console.error("Validation failed:", error);
      }
    } else if (currentStep === 1) {
      if (pdfUrl) {
        setCurrentStep(2);
      } else {
        message.error("Please upload a PDF first");
      }
    } else if (currentStep === 2) {
      if (signatureFields.length > 0) {
        setCurrentStep(3);
      } else {
        message.error("Please add at least one signature field");
      }
    }
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleCancel = () => {
    form.resetFields();
    setCurrentStep(0);
    setPdfFile(null);
    setPdfUrl("");
    setSignatureFields([]);
    onClose();
  };

  return (
    <StyledModal
      title={editLease ? "Edit Lease" : "Create New Lease"}
      open={isOpen}
      onCancel={handleCancel}
      width={800}
      centered
      footer={null}
      destroyOnClose
    >
      <ModalBodyInner>
        <Steps current={currentStep} className="lease-modal-steps">
          {steps.map((step) => (
            <Step key={step.title} title={step.title} icon={step.icon} />
          ))}
        </Steps>

        <Form
          form={form}
          layout="vertical"
          size="large"
          className="lease-modal-form"
        >
          {renderStepContent()}
        </Form>

        <Divider className="lease-modal-step-divider" />
      </ModalBodyInner>

      <LeaseModalFooter>
        <Space>
          {currentStep > 0 && (
            <Button size="large" onClick={handlePrev}>
              Previous
            </Button>
          )}

          {currentStep < steps.length - 1 ? (
            <Button type="primary" size="large" onClick={handleNext}>
              Next
            </Button>
          ) : (
            (() => {
              const finishBtn = (
                <Button
                  type="primary"
                  size="large"
                  danger={!depositValid}
                  onClick={handleFinish}
                  loading={loading}
                >
                  {editLease ? "Update Lease" : "Create Lease"}
                </Button>
              );
              return !depositValid && complianceConfig?.depositStatute ? (
                <Tooltip title={complianceConfig.depositStatute}>
                  <span>{finishBtn}</span>
                </Tooltip>
              ) : (
                finishBtn
              );
            })()
          )}
        </Space>
      </LeaseModalFooter>
    </StyledModal>
  );
};

/* Match RentDashboard confirm / DetailsModal: 56px header, flush content, full-width footer */
const ModalBodyInner = styled.div`
  padding: 24px 24px 0;
  background: #ffffff;
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;

  @media ${device.mobile} {
    padding: 16px 16px 0;
  }
`;

const LeaseModalFooter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px 24px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  background: #fafafa;
  flex-shrink: 0;

  @media ${device.mobile} {
    padding: 12px 16px;
  }

  .ant-btn-primary {
    font-weight: 600;
  }
`;

const CONTROL_H = 40;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    padding: 0 !important;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    max-height: calc(100dvh - 32px);
    display: flex;
    flex-direction: column;
  }

  .ant-modal-header {
    margin-bottom: 0 !important;
    background: linear-gradient(135deg, var(--color-primary) 0%, #4a7c59 100%);
    border-bottom: none;
    border-radius: 12px 12px 0 0;
    padding: 16px 20px;
    display: flex;
    align-items: center;
    position: relative;
    min-height: 56px;
  }

  .ant-modal-title {
    color: #ffffff !important;
    font-weight: 600;
    font-size: 18px;
    line-height: 1.2;
    margin: 0;
    padding-right: 48px;
  }

  .ant-modal-header .ant-modal-title,
  .ant-modal-header .ant-modal-title * {
    color: #ffffff !important;
  }

  /* Pin to top of modal content (green header). NOT top: 50% - that centers on full modal height. */
  .ant-modal-close {
    color: #ffffff !important;
    font-size: 16px;
    top: 12px !important;
    right: 20px !important;
    inset-inline-end: 20px !important;
    margin: 0 !important;
    transform: none !important;
    width: 32px !important;
    height: 32px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    background: transparent !important;
    border-radius: 0 !important;
    border: none !important;
    transition:
      background 0.2s,
      border-color 0.2s,
      transform 0.2s !important;
  }

  .ant-modal-close:hover {
    color: #ffffff !important;
    background: transparent !important;
    border-color: transparent !important;
  }

  .ant-modal-close .anticon,
  .ant-modal-close .ant-modal-close-icon {
    color: #ffffff !important;
  }

  .ant-modal-close .anticon svg,
  .ant-modal-close .ant-modal-close-x svg {
    fill: #ffffff !important;
    color: #ffffff !important;
  }

  .ant-modal-body {
    padding: 0 !important;
    background: #ffffff;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .lease-modal-steps {
    margin-bottom: 24px;
  }

  .lease-modal-step-divider {
    margin: 24px 0 0;
  }

  .lease-modal-form .lease-terms-row .ant-form-item-label {
    padding-bottom: 2px !important;
  }

  .lease-modal-form .lease-terms-row .ant-form-item {
    margin-bottom: 8px;
  }

  /* Equal control height (${CONTROL_H}px) - Input, Select, DatePicker, InputNumber */
  .lease-modal-form .ant-input,
  .lease-modal-form .ant-input-affix-wrapper,
  .lease-modal-form .ant-picker,
  .lease-modal-form .ant-input-number,
  .lease-modal-form .ant-input-number-affix-wrapper {
    min-height: ${CONTROL_H}px !important;
  }

  .lease-modal-form .ant-input {
    padding: 8px 11px;
    line-height: 22px;
  }

  .lease-modal-form .ant-input-affix-wrapper,
  .lease-modal-form .ant-input-number-affix-wrapper {
    padding-inline: 11px;
    display: flex;
    align-items: center;
    border: 2px solid var(--color-border, #d9d9d9) !important;
    border-radius: 8px !important;
    box-shadow: none !important;
  }

  .lease-modal-form .ant-input-affix-wrapper .ant-input,
  .lease-modal-form .ant-input-number-affix-wrapper .ant-input-number-input {
    min-height: auto !important;
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
  }

  .lease-modal-form
    .ant-input-number-affix-wrapper
    > .ant-input-number {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
  }

  .lease-modal-form .ant-input-number .ant-input-number-input-wrap {
    height: ${CONTROL_H - 2}px;
  }

  .lease-modal-form .ant-input-number .ant-input-number-input {
    height: ${CONTROL_H - 2}px;
    line-height: ${CONTROL_H - 2}px;
  }

  .lease-modal-form .ant-picker .ant-picker-input > input {
    line-height: 22px;
  }

  .lease-modal-form .ant-picker {
    padding: 4px 11px;
  }

  .lease-modal-form
    .ant-select:not(.ant-select-customize-input)
    .ant-select-selector {
    min-height: ${CONTROL_H}px !important;
    height: ${CONTROL_H}px !important;
    display: flex !important;
    align-items: center !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }

  .lease-modal-form
    .ant-select-single.ant-select-show-arrow
    .ant-select-selection-item,
  .lease-modal-form
    .ant-select-single.ant-select-show-arrow
    .ant-select-selection-placeholder {
    line-height: ${CONTROL_H - 2}px !important;
    display: flex;
    align-items: center;
    font-size: 0.875rem !important;
    font-weight: 400 !important;
  }

  .lease-modal-form .ant-form-item {
    margin-bottom: 20px;
  }

  .lease-modal-form .ant-form-item-label {
    padding-bottom: 2px !important;
  }

  .lease-modal-form .ant-form-item-label > label {
    font-weight: 500;
  }

  .lease-modal-form .ant-checkbox-wrapper {
    align-items: center;
    min-height: ${CONTROL_H}px;
  }

  .lease-modal-form .pets-allowed-field {
    text-align: center;

    .ant-form-item-label > label {
      width: 100%;
      justify-content: center;
    }

    .ant-form-item-control-input-content {
      display: flex;
      justify-content: center;
    }
  }

  .lease-modal-form .ant-checkbox-inner {
    width: 18px;
    height: 18px;
    border-width: 2px;
    border-color: var(--color-border, #d9d9d9);
  }

  .lease-modal-form .ant-checkbox-checked .ant-checkbox-inner {
    border-color: var(--color-primary);
    background-color: var(--color-primary);
  }

  .ant-steps-item-icon {
    background: var(--color-primary);
    border-color: var(--color-primary);
  }

  .ant-steps-item-process .ant-steps-item-icon {
    background: var(--color-primary);
    border-color: var(--color-primary);
  }

  .ant-steps-item-finish .ant-steps-item-icon {
    background: var(--color-success);
    border-color: var(--color-success);
  }

  @media ${device.mobile} {
    .ant-modal-content {
      max-height: calc(100dvh - 24px);
    }

    .ant-modal-header {
      padding: 14px 16px;
      min-height: 52px;
    }
  }
`;

export default LeaseModal;
